import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { authService } from '../services/authService'
import { LoginRequest, AuthState } from '../types/auth'

interface AuthContextType extends AuthState {
  loading: boolean
  error: string | null
  login: (credentials: LoginRequest) => Promise<void>
  logout: () => void
  checkAuth: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: authService.isAuthenticated(),
    token: authService.getToken(),
    user: authService.getUser()
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const login = useCallback(async (credentials: LoginRequest) => {
    setLoading(true)
    setError(null)
    try {
      const response = await authService.login(credentials)
      setAuthState({
        isAuthenticated: true,
        token: response.token,
        user: {
          username: response.username,
          role: response.role,
          facilityId: response.facilityId,
          tenantId: response.tenantId,
          operatingEntityId: response.operatingEntityId,
          availableFacilities: response.availableFacilities
        }
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    authService.logout()
    setAuthState({
      isAuthenticated: false,
      token: null,
      user: null
    })
  }, [])

  const checkAuth = useCallback(() => {
    const isAuth = authService.isAuthenticated()
    setAuthState({
      isAuthenticated: isAuth,
      token: authService.getToken(),
      user: authService.getUser()
    })
  }, [])

  return (
    <AuthContext.Provider value={{ ...authState, loading, error, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
